import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import DashboardLayout from "./Dashboard/DashboardLayout";
import DashboardHome from "./Dashboard/DashboardHome";
import SurveyList from "./Dashboard/SurveyList";
import SurveyForm from "./Dashboard/SurveyForm";
import SurveyShow from "./Dashboard/SurveyShow";
import SurveyReport from "./Dashboard/SurveyReport";
import ManagerList from "./Dashboard/ManagerList";
import ManagerForm from "./Dashboard/ManagerForm";
import AnonymousSurvey from "./Public/AnonymousSurvey";
import { authAPI } from "../utils/api";

const LandingPage = () => {
  const styles = {
    pageWrapper: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: "2rem",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto"
    },
    header: {
      textAlign: "center",
      color: "white",
      paddingTop: "4rem",
      paddingBottom: "3rem"
    },
    logo: {
      fontSize: "4rem",
      marginBottom: "1rem"
    },
    title: {
      fontSize: "3.5rem",
      fontWeight: "700",
      marginBottom: "1rem",
      textShadow: "2px 2px 4px rgba(0,0,0,0.2)"
    },
    subtitle: {
      fontSize: "1.5rem",
      fontWeight: "300",
      opacity: "0.95",
      maxWidth: "700px",
      margin: "0 auto"
    },
    heroSection: {
      backgroundColor: "white",
      borderRadius: "20px",
      padding: "3rem",
      marginBottom: "3rem",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      textAlign: "center"
    },
    heroTitle: {
      fontSize: "2rem",
      color: "#2d3748",
      marginBottom: "1.5rem",
      fontWeight: "600"
    },
    heroText: {
      fontSize: "1.2rem",
      color: "#4a5568",
      lineHeight: "1.8",
      maxWidth: "800px",
      margin: "0 auto 2.5rem"
    },
    buttonContainer: {
      display: "flex",
      gap: "1.5rem",
      justifyContent: "center",
      flexWrap: "wrap"
    },
    primaryButton: {
      padding: "1rem 3rem",
      fontSize: "1.2rem",
      fontWeight: "600",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
      border: "none",
      borderRadius: "50px",
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-block",
      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
      transition: "all 0.3s ease",
      transform: "translateY(0)"
    },
    secondaryButton: {
      padding: "1rem 3rem",
      fontSize: "1.2rem",
      fontWeight: "600",
      backgroundColor: "white",
      color: "#667eea",
      border: "3px solid #667eea",
      borderRadius: "50px",
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-block",
      transition: "all 0.3s ease",
      transform: "translateY(0)"
    },
    footer: {
      textAlign: "center",
      color: "white",
      marginTop: "4rem",
      paddingBottom: "2rem",
      opacity: "0.9"
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={{textAlign: 'center', marginBottom: '2rem'}}>
            <img 
              src="/bluecoding-logo.png" 
              alt="Blue Coding" 
              style={{maxWidth: '250px', height: 'auto'}}
            />
          </div>
          <h1 style={styles.title}>SurveyPilot</h1>
          <p style={styles.subtitle}>
            Empowering Organizations Through Data-Driven Manager Development
          </p>
        </div>

        <div style={styles.heroSection}>
          <h2 style={styles.heroTitle}>
            Transform Your Leadership Through Anonymous Feedback
          </h2>
          <p style={styles.heroText}>
            At Blue Coding, we’re committed to strong leadership. Our internal feedback tool gathers anonymous input across key leadership areas, helping managers recognize strengths, identify growth opportunities, and continuously improve team support.
          </p>
          
          <div style={styles.buttonContainer}>
            <a 
              href="/admin/signin" 
              style={styles.primaryButton}
              onMouseOver={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
              }}
            >
              Login
            </a>
          </div>
        </div>

        <div style={styles.footer}>
          <p>© 2025 SurveyPilot. Building better managers, one survey at a time.</p>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setCurrentUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setCurrentUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (logoutPath) => {
    try {
      await fetch(logoutPath, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || '',
        },
        credentials: 'same-origin'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Clear state and force full page reload
      setCurrentUser(null);
      setIsAuthenticated(false);
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
  };

  if (loading) {
    return <div style={{padding: '2rem', textAlign: 'center'}}>Loading...</div>;
  }

  return (
    <Router>
      <Routes>
        {/* Anonymous survey response route - no authentication required */}
        <Route path="/survey/respond/:token" element={<AnonymousSurvey />} />
        
        {!isAuthenticated ? (
          <>
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <>
            {/* All users are admins now - redirect to admin dashboard */}
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            
            <Route path="/admin/dashboard" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <DashboardHome currentUser={currentUser} />
              </DashboardLayout>
            } />
            <Route path="/admin/surveys" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <SurveyList currentUser={currentUser} />
              </DashboardLayout>
            } />
            <Route path="/admin/surveys/new" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <SurveyForm />
              </DashboardLayout>
            } />
            <Route path="/admin/surveys/:id" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <SurveyShow />
              </DashboardLayout>
            } />
            <Route path="/admin/surveys/:id/edit" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <SurveyForm />
              </DashboardLayout>
            } />
            <Route path="/admin/surveys/:id/stats" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <SurveyReport />
              </DashboardLayout>
            } />
            <Route path="/admin/managers" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <ManagerList />
              </DashboardLayout>
            } />
            <Route path="/admin/managers/new" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <ManagerForm />
              </DashboardLayout>
            } />
            <Route path="/admin/managers/:id/edit" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <ManagerForm />
              </DashboardLayout>
            } />
            <Route path="/admin/analytics" element={
              <DashboardLayout currentUser={currentUser} onLogout={handleLogout}>
                <div style={{padding: '2rem', backgroundColor: 'white', borderRadius: '8px'}}>
                  <h2>Overall Analytics</h2>
                  <p>View individual survey reports from the Surveys page.</p>
                </div>
              </DashboardLayout>
            } />
            
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </>
        )}
      </Routes>
    </Router>
  );
};

export default App;
